import React, { useState } from "react";
import api from "../lib/api";
import { X, Loader2, Save, HelpCircle } from "lucide-react";
import { SECURITY_QUESTIONS } from "../lib/securityQuestions";

const SecurityQuestionModal = ({ open, initialQuestion, onClose, onSaved }) => {
  const [customMode, setCustomMode] = useState(
    initialQuestion && !SECURITY_QUESTIONS.includes(initialQuestion)
  );
  const [question, setQuestion] = useState(
    initialQuestion || SECURITY_QUESTIONS[0]
  );
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!question.trim() || !answer.trim()) {
      setErr("Question and answer required");
      return;
    }
    setErr("");
    setBusy(true);
    try {
      await api.put("/auth/security-question", {
        secret_question: question.trim(),
        secret_answer: answer,
      });
      onSaved?.();
      onClose();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <HelpCircle size={18} className="text-[#14B8A6]" />
            <h3 className="font-semibold text-slate-800">Security Question</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <p className="text-sm text-slate-600">
            Set a security question so you can self-reset your password later.
          </p>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Question</label>
            {customMode ? (
              <input
                type="text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Type your own question"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2A5F]"
              />
            ) : (
              <select
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0F2A5F]"
              >
                {SECURITY_QUESTIONS.map((q) => <option key={q}>{q}</option>)}
              </select>
            )}
            <button
              type="button"
              onClick={() => { setCustomMode((v) => !v); setQuestion(customMode ? SECURITY_QUESTIONS[0] : ""); }}
              className="text-xs text-[#0F2A5F] hover:underline mt-1"
            >
              {customMode ? "Choose from list" : "Write custom question"}
            </button>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Answer</label>
            <input
              type="text"
              required
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Answer (case-insensitive)"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2A5F]"
            />
            <p className="text-[11px] text-slate-400 mt-1">Save this somewhere safe. You'll need it to reset your password.</p>
          </div>

          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">{err}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancel</button>
            <button type="submit" disabled={busy} className="px-4 py-2 text-sm bg-[#14B8A6] hover:bg-[#0F8A7D] disabled:opacity-70 text-white rounded-md font-medium inline-flex items-center gap-2">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SecurityQuestionModal;
